import React, { useMemo, useState } from "react";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";

import {
  GalleryItem,
  GalleryListElement,
} from "src/app/components/Gallery/components/GalleryItem/GalleryItem";
import GalleryContentViewer from "src/app/components/Gallery/components/GalleryContentViewer/GalleryContentViewer";

import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { Button } from "src/app/components/Button/Button";
import {
  capitalizeFirstLetter,
  formatNumber,
  toFixedIfNecessary,
} from "src/utils/methods";
import { ReactComponent as SortIcon } from "src/images/up-down.svg";
import { ReactComponent as TickIcon } from "src/images/tick.svg";
import { ReactComponent as ShareIcon } from "src/images/share-alt.svg";
import { useHistory, useLocation } from "react-router";
import HorizontalScroller from "src/app/components/Tabs/components/HorizontalScroller";
import moment from "moment";
import { SocialProviderIcon2 } from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/SocialProviderIcon2/SocialProviderIcon2";
import Pictogram, {
  PictogramVariants,
} from "src/app/components/Pictogram/Pictogram";
import { Img } from "src/app/components/Img/Img";
import { SocialProvider, convertTikTokToWorkspaceVersion } from "src/types";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import publicationPlaceholder from "src/images/eye-placeholder.svg";

import "./IDPublications.scss";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

interface Props {
  data: {
    date: GalleryListElement[];
    er: GalleryListElement[];
  };
  showMoreTo?: string;
  showControls?: boolean;
  titleId?: string;
  titleDefaultMessage?: string;
  renderItem?: (
    item: GalleryListElement,
    index: number,
    onThumbnailClick: (index: number) => void,
  ) => React.ReactNode;
}

type SortBy = "date" | "er";

export const IDPublications = (props: Props) => {
  const {
    data,
    showMoreTo,
    showControls = true,
    titleId = "ws_publications",
    titleDefaultMessage = "Publications",
    renderItem,
  } = props;

  const [slideIndex, setSlideIndex] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>("date");

  const { printing, setup } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const history = useHistory();
  const { pathname } = useLocation();

  const normalizeKeyword = (keyword: string, prefix: "#" | "@") => {
    const trimmed = keyword?.trim();

    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith(prefix)) {
      return trimmed;
    }

    return `${prefix}${trimmed.replace(/^[@#]+/, "")}`;
  };

  const highlightedKeywords = useMemo(() => {
    const keywords = [
      ...(setup?.hashtags || [])
        .map((keyword) => normalizeKeyword(keyword, "#"))
        .filter((keyword): keyword is string => Boolean(keyword)),
      ...(setup?.mentions || [])
        .map((keyword) => normalizeKeyword(keyword, "@"))
        .filter((keyword): keyword is string => Boolean(keyword)),
    ];

    return Array.from(new Set(keywords));
  }, [setup?.hashtags, setup?.mentions]);

  const list = data[sortBy];
  const sortByOptions: { label: string; value: SortBy }[] = [
    {
      label: "Date",
      value: "date",
    },
    {
      label: "ER",
      value: "er",
    },
  ];

  const renderStatValue = (value: number, type: string) => {
    if (type === "percent") {
      return `${toFixedIfNecessary((value ?? 0) * 100, 2)}%`;
    }

    return formatNumber(value);
  };

  const renderPdfItem = (item: GalleryListElement) => {
    const stats = {
      er: { value: item.engagementRate, type: "percent" },
      likes: { value: item.likes, type: "number" },
      views: { value: item.views, type: "number" },
      comments: { value: item.comments, type: "number" },
    };
    const visibleStats = Object.entries(stats).filter(([, stat]) => stat.value);

    return (
      <div
        className="id-publications__pdf-item"
        data-qa={item.url}
        key={item.url}
      >
        <div className="id-publications__pdf-provider">
          <div className="id-publications__pdf-creator">
            {item.creatorData && (
              <Img
                src={item.creatorData.profilePictureUrl}
                fallbackImageSrc={creatorPlaceholder}
                alt=""
                referrerPolicy="no-referrer"
              />
            )}
            <span>{item.creatorData?.username}</span>
          </div>

          <SocialProviderIcon2 provider={item.socialProvider} />
        </div>

        <Img
          className="id-publications__pdf-thumbnail"
          src={item.thumbUrl}
          fallbackImageSrc={publicationPlaceholder}
          alt="thumbnail"
          referrerPolicy="no-referrer"
          // TikTok-only HEIC recovery, see task:
          // https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
          allowHeicRecovery={
            convertTikTokToWorkspaceVersion(item.socialProvider) ===
            SocialProvider.TikTok
          }
          // Rendered at 302px (.id-publications__pdf-thumbnail in IDPublications.scss).
          heicRecoveryMaxDimension={302}
        />

        <div className="id-publications__pdf-data">
          <span className="id-publications__pdf-data-item">
            {item.publishedDate &&
              moment(item.publishedDate).format("DD.MM.YYYY").toString()}
          </span>

          {visibleStats.map(([key, stat]) => (
            <span className="id-publications__pdf-data-item" key={key}>
              <Pictogram
                identifier={`Social/${key}`}
                variant={PictogramVariants.CoolGrey}
              />
              {renderStatValue(stat.value as number, stat.type)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderList = () =>
    list.map((item, index) =>
      renderItem ? (
        <React.Fragment key={item.url}>
          {renderItem(item, index, setSlideIndex)}
        </React.Fragment>
      ) : (
        <GalleryItem
          data={item}
          index={index}
          setSlideIndex={setSlideIndex}
          displayCheckbox={false}
          selectedPostIds={[]}
          setSelectedPostIds={() => {}}
        />
      ),
    );

  return (
    <div className="id-publications">
      <span className="id-publications__header">
        <IDHFormattedMessage
          id={titleId}
          defaultMessage={titleDefaultMessage}
        />

        {!printing && showControls && (
          <div className="id-publications__header-buttons">
            <Dropdown>
              {(open, setOpen) => (
                <>
                  <Button
                    variant="white"
                    size="small"
                    onClick={() => setOpen(!open)}
                  >
                    <SortIcon />
                    Sort by: {capitalizeFirstLetter(sortBy)}
                  </Button>

                  <DropdownMenu isOpen={open}>
                    {sortByOptions.map((item) => (
                      <DropdownMenuItem
                        onClick={() => {
                          setSortBy(item.value);
                          setOpen(false);
                        }}
                      >
                        {item.label}
                        {sortBy === item.value && <TickIcon />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenu>
                </>
              )}
            </Dropdown>

            <Button
              variant="white-with-light-grey-border"
              size="small"
              onClick={() =>
                showMoreTo
                  ? history.push(showMoreTo)
                  : history.push({
                      pathname,
                      search: `?type=all&value=${sortBy}`,
                    })
              }
            >
              Show more
              <ShareIcon />
            </Button>
          </div>
        )}
      </span>

      {printing ? (
        <div className="id-publications__container id-publications__container--wrap">
          {list.map(renderPdfItem)}
        </div>
      ) : (
        <HorizontalScroller disableWheelScroll>
          <div key={sortBy} className="id-publications__container">
            {renderList()}
          </div>
        </HorizontalScroller>
      )}

      <GalleryContentViewer
        data={list}
        slideIndex={slideIndex}
        clearSlideIndex={() => setSlideIndex(null)}
        creatorData={{
          username: "unknown",
          profilePictureUrl: undefined,
        }}
        keywordsToHighlight={
          highlightedKeywords.length > 0 ? highlightedKeywords : undefined
        }
      />
    </div>
  );
};
